import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserSession } from '../contexts/UserContext.js';
import './LocalDevPanel.css';

const LocalDevPanel = () => {
  const navigate = useNavigate();
  const { userSession, updateUserSession } = useUserSession();
  const [isVisible, setIsVisible] = useState(false);
  const [mockUserType, setMockUserType] = useState('Mortal');
  const [mockModalStep, setMockModalStep] = useState(1);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const [mockVerificationStep, setMockVerificationStep] = useState('phone');

  // Show on localhost & staging/preview environments, but never on prod
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isStaging = hostname.includes('vercel.app') || hostname.includes('staging');
  const isProduction = hostname.includes('intellegacy.app') && !hostname.includes('staging');
  const shouldShowDevPanel = (isLocalhost || isStaging) && !isProduction;

  useEffect(() => {
    if (!shouldShowDevPanel) return;

    // Initialize with current user session data
    if (userSession.authorizationGrade) {
      setMockUserType(userSession.authorizationGrade);
    }
  }, [userSession.authorizationGrade, shouldShowDevPanel]);

  if (!shouldShowDevPanel) {
    return null; // Don't render anything in production
  }

  const handleUserTypeChange = (newType) => {
    setMockUserType(newType);
    
    // Update the user session to simulate different user types
    updateUserSession({
      authorizationGrade: newType,
      // Set appropriate defaults for each user type
      hasPaid: newType === 'God' || newType === 'Immortal',
      planType: newType === 'God' || newType === 'Immortal' ? 'Elite' : 'Unassigned',
      termsAccepted: newType === 'God' || newType === 'Immortal',
      // Preserve user verification state if in verification mode, otherwise set complete
      user: showVerificationPanel ? userSession.user : { 
        email: 'test@example.com', 
        isEmailVerified: true, 
        phone: '+1234567890'
      },
      isLoaded: true
    });
  };

  const handleModalStepChange = (step) => {
    setMockModalStep(step);
    
    // Update user session to simulate being at different modal steps
    const stepConfigs = {
      1: { reasonToJoin: '', planType: 'Unassigned', termsAccepted: false, hasPaid: false },
      2: { reasonToJoin: 'Test reason', planType: 'Unassigned', termsAccepted: false, hasPaid: false },
      3: { reasonToJoin: 'Test reason', planType: 'Elite', termsAccepted: true, hasPaid: false },
      4: { reasonToJoin: 'Test reason', planType: 'Elite', termsAccepted: true, hasPaid: true }
    };
    
    updateUserSession({
      ...stepConfigs[step],
      // Bypass verification for normal modal steps with mock
      user: { 
        email: 'test@example.com', 
        isEmailVerified: true, 
        phone: '+1234567890'
        // No devVerificationStep - normal modal flow
      },
      isLoaded: true
    });
  };

  const handleVerificationStepChange = (verificationStep) => {
    setMockVerificationStep(verificationStep);
    
    // Update user session to simulate verification states that will trigger the right UI
    const verificationConfigs = {
      'phone': { 
        user: { 
          email: null,  // No email to trigger phone step first
          isEmailVerified: false, 
          phone: null,
          devVerificationStep: 'phone'  // Force phone step UI
        }
      },
      'email-request': { 
        user: { 
          email: 'test@example.com',  // Has email but not verified
          isEmailVerified: false, 
          phone: '+1234567890',
          devVerificationStep: 'email-request'  // Force email request UI
        }
      },
      'email-verify': { 
        user: { 
          email: 'test@example.com',  // Has email but not verified
          isEmailVerified: false, 
          phone: '+1234567890',
          devVerificationStep: 'email-verify'  // Force email verify UI
        }
      },
      'complete': { 
        user: { 
          email: 'test@example.com', 
          isEmailVerified: true,  // Everything verified
          phone: '+1234567890'
          // No devVerificationStep - should go to normal modal
        }
      }
    };
    
    updateUserSession({
      ...verificationConfigs[verificationStep],
      isLoaded: true,
      // Reset modal state to ensure fresh start
      reasonToJoin: '',
      termsAccepted: false,
      hasPaid: false,
      planType: 'Unassigned'
    });
  };

  const enableVerificationMode = () => {
    setShowVerificationPanel(true);
    // Set user to need verification - start at phone step
    updateUserSession({
      user: { 
        email: null,  // No email to trigger phone step first
        isEmailVerified: false, 
        phone: null,
        devVerificationStep: 'phone'  // Force phone step UI
      },
      isLoaded: true,
      // Reset other modal states
      reasonToJoin: '',
      termsAccepted: false,
      hasPaid: false,
      planType: 'Unassigned'
    });
    setMockVerificationStep('phone');
  };

  const navigationButtons = [
    { label: 'Landing', path: '/' },
    { label: 'Home', path: '/home' },
    { label: 'Onboarding', path: '/onboarding' }
  ];

  const userTypes = ['Mortal', 'God', 'Immortal', 'Unassigned'];

  return (
    <div className="local-dev-panel">
      <button 
        className="dev-panel-toggle"
        onClick={() => setIsVisible(!isVisible)}
      >
        🛠️ DEV
      </button>
      
      {isVisible && (
        <div className="dev-panel-content">
          <div className="dev-panel-header">
            <h3>Local Dev Tool</h3>
            <button onClick={() => setIsVisible(false)} className="close-btn">×</button>
          </div>
          
          <div className="dev-section">
            <h4>User Type Simulation</h4>
            <div className="button-group">
              {userTypes.map(type => (
                <button
                  key={type}
                  className={`dev-btn ${mockUserType === type ? 'active' : ''}`}
                  onClick={() => handleUserTypeChange(type)}
                >
                  {type}
                </button>
              ))}
            </div>
            <small>Current: {userSession.authorizationGrade || 'None'}</small>
          </div>

          {!showVerificationPanel ? (
            <div className="dev-section">
              <h4>Modal Step Simulation</h4>
              <div className="button-group">
                {[1, 2, 3, 4].map(step => (
                  <button
                    key={step}
                    className={`dev-btn ${mockModalStep === step ? 'active' : ''}`}
                    onClick={() => handleModalStepChange(step)}
                  >
                    Step {step}
                  </button>
                ))}
              </div>
              <small>
                Step 1: Reason | Step 2: Plan | Step 3: Payment | Step 4: Complete
              </small>
            </div>
          ) : (
            <div className="dev-section">
              <h4>Verification Step Simulation</h4>
              <div className="button-group">
                {['phone', 'email-request', 'email-verify', 'complete'].map(step => (
                  <button
                    key={step}
                    className={`dev-btn ${mockVerificationStep === step ? 'active' : ''}`}
                    onClick={() => handleVerificationStepChange(step)}
                  >
                    {step === 'phone' ? 'Phone' : 
                     step === 'email-request' ? 'Email Req' : 
                     step === 'email-verify' ? 'Code Verify' : 
                     'Complete'}
                  </button>
                ))}
              </div>
              <small>
                Phone → Email Request → Code Verify → Complete
              </small>
              <button 
                className="dev-btn reset-btn"
                style={{ marginTop: '8px', width: '100%' }}
                onClick={() => {
                  setShowVerificationPanel(false);
                  // Reset to normal modal state with no verification needed
                  updateUserSession({
                    user: { 
                      email: 'test@example.com', 
                      isEmailVerified: true, 
                      phone: '+1234567890'
                      // No devVerificationStep - normal modal flow
                    },
                    reasonToJoin: 'Test reason',
                    termsAccepted: false,
                    hasPaid: false,
                    planType: 'Unassigned',
                    isLoaded: true
                  });
                  setMockModalStep(1);
                }}
              >
                Back to Modal Steps
              </button>
            </div>
          )}

          <div className="dev-section">
            <h4>Quick Navigation</h4>
            <div className="button-group">
              {navigationButtons.map(({ label, path }) => (
                <button
                  key={path}
                  className="dev-btn nav-btn"
                  onClick={() => navigate(path)}
                >
                  {label}
                </button>
              ))}
              <button
                className={`dev-btn nav-btn ${showVerificationPanel ? 'active' : ''}`}
                onClick={() => enableVerificationMode()}
              >
                Detail Verification
              </button>
            </div>
          </div>

          <div className="dev-section">
            <h4>Current State</h4>
            <div className="state-info">
              <div><strong>Environment:</strong> {
                isLocalhost ? 'Localhost Dev' : 
                isStaging ? 'Staging/Preview' : 
                'Unknown'
              }</div>
              <div><strong>User Type:</strong> {userSession.authorizationGrade}</div>
              <div><strong>Has Paid:</strong> {userSession.hasPaid ? 'Yes' : 'No'}</div>
              <div><strong>Plan:</strong> {userSession.planType}</div>
              <div><strong>Terms:</strong> {userSession.termsAccepted ? 'Accepted' : 'Not Accepted'}</div>
              <div><strong>Reason:</strong> {userSession.reasonToJoin || 'None'}</div>
              {showVerificationPanel && (
                <>
                  <div><strong>Email:</strong> {userSession.user?.email || 'None'}</div>
                  <div><strong>Email Verified:</strong> {userSession.user?.isEmailVerified ? 'Yes' : 'No'}</div>
                  <div><strong>Phone:</strong> {userSession.user?.phone || 'None'}</div>
                  <div><strong>Verification Step:</strong> {mockVerificationStep}</div>
                  <div><strong>Needs Verification:</strong> {
                    (!userSession.user?.email || !userSession.user?.isEmailVerified || !userSession.user?.phone) ? 'Yes' : 'No'
                  }</div>
                </>
              )}
            </div>
          </div>

          <div className="dev-section">
            <button 
              className="dev-btn reset-btn"
              onClick={() => {
                updateUserSession({
                  authorizationGrade: 'Mortal',
                  hasPaid: false,
                  planType: 'Unassigned',
                  termsAccepted: false,
                  reasonToJoin: '',
                  user: { 
                    email: 'test@example.com', 
                    isEmailVerified: true, 
                    phone: '+1234567890' 
                  },
                  isLoaded: true
                });
                setMockUserType('Mortal');
                setMockModalStep(1);
                setShowVerificationPanel(false);
                setMockVerificationStep('phone');
              }}
            >
              Reset to Default
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalDevPanel;
